import { useState } from "react";
import { SectionShell, Panel, Note } from "@/components/dash/primitives";
import { ChartTip, CHART, usd } from "@/components/dash/chartkit";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, LabelList, Cell,
} from "recharts";

const WEEKLY = [
  { key: "shanghai", name: "SHA–SYD", color: CHART.blue },
  { key: "global", name: "Comprehensive (SHA–WORLD)", color: CHART.violet },
  { key: "laem", name: "Xeneta LCH–SYD", color: CHART.orange },
  { key: "mna", name: "MNA NAC (LCH–SYD)", color: "#312e81", dashed: true },
];

export default function Rates({ data, num, title }) {
  const [visible, setVisible] = useState({ shanghai: true, global: true, laem: true, mna: true });

  const weekly = data.weekly_labels.map((l, i) => ({
    label: l,
    shanghai: data.weekly_shanghai[i],
    global: data.weekly_global[i],
    laem: data.weekly_laem[i],
    mna: data.weekly_mna ? data.weekly_mna[i] : null,
  }));

  return (
    <SectionShell id="rates" num={num} title={title}>
      <p className="text-slate-700 leading-relaxed max-w-4xl mb-8">{data.intro}</p>

      <Note accent>{data.thailand_callout}</Note>

      <div className="mt-8">
        <Panel
          title="Weekly rate trends"
          subtitle="SHA–SYD, Comprehensive (SHA–WORLD), Xeneta market rate & MNA NAC benchmark — US$/TEU"
          source="Source: Xeneta market rate data & MNA NAC benchmark, weekly, 3 Jan 2025 – 18 Sep 2026"
          testid="chart-weekly-rates"
          csv={{ name: "Weekly rates", columns: ["Week", "SHA-SYD", "Comprehensive (SHA-WORLD)", "Xeneta LCH-SYD", "MNA NAC (LCH-SYD)"], rows: weekly.map((d) => [d.label, d.shanghai, d.global, d.laem, d.mna]) }}
        >
          <div className="flex flex-wrap gap-2 mb-4">
            {WEEKLY.map((s) => (
              <button
                key={s.key}
                data-testid={`weekly-toggle-${s.key}`}
                onClick={() => setVisible((v) => ({ ...v, [s.key]: !v[s.key] }))}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  visible[s.key] ? "border-slate-300 bg-white text-slate-700" : "border-slate-200 bg-slate-50 text-slate-400"
                }`}
              >
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: visible[s.key] ? s.color : "#cbd5e1" }} />
                {s.name}
              </button>
            ))}
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={weekly} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 10 }} interval="preserveStartEnd" minTickGap={24} />
              <YAxis tickFormatter={(v) => `$${v}`} tickLine={false} axisLine={false} tick={{ fill: "#64748b" }} width={52} />
              <Tooltip content={<ChartTip formatter={usd} />} />
              {WEEKLY.map((s) =>
                visible[s.key] ? (
                  <Line key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={s.color} strokeWidth={2.25} strokeDasharray={s.dashed ? "5 4" : undefined} dot={false} activeDot={{ r: 5 }} connectNulls />
                ) : null
              )}
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      </div>
      <Note accent>{data.weekly_note}</Note>

      <div className="mt-8">
        <Panel
          title="Vessel charter rates (US$/day), Jan → Oct 2026"
          subtitle="12-month time-charter hire by vessel size — monthly"
          source="Source: New ConTex / Harper Petersen (HARPEX), Jan–Jul 2026"
          testid="chart-charter"
          csv={{ name: "Charter rates", columns: ["Month", "1,700 TEU US$/day", "2,500 TEU US$/day"], rows: data.charter.map((d) => [d.month, d.feeder1700, d.mid2500]) }}
        >
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.charter} margin={{ top: 16, right: 16, left: 8, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="month" tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 11 }} />
              <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 11 }} width={48} domain={["dataMin - 3000", "dataMax + 3000"]} />
              <Tooltip cursor={{ stroke: "#e2e8f0" }} content={<ChartTip formatter={(v) => `$${Number(v).toLocaleString()}/day`} />} />
              <Line type="monotone" dataKey="feeder1700" name="1,700 TEU feeder" stroke={CHART.orange} strokeWidth={2.25} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="mid2500" name="2,500 TEU" stroke={CHART.blue} strokeWidth={2.25} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      </div>
      <Note>{data.charter_note}</Note>
      <Note>{data.drewry_note}</Note>
    </SectionShell>
  );
}
